import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "@helpers/translate";
import "./index.sass";

const FILTER_OPTIONS = [
  { label: "common.today", value: "today" },
  { label: "common.yesterday", value: "yesterday" },
  { label: "common.last_7_days", value: "last_7_days" },
  { label: "common.last_30_days", value: "last_30_days" },
  { label: "common.current_month", value: "current_month" },
  { label: "common.previous_month", value: "previous_month" },
  { label: "common.all_time", value: "all_time" },
];

const DEFAULT_STATS = {
  pnl: {},
  averagePnl: {},
  averagePnlPercent: {},
  holdingTime: {},
  positions: {},
  profitLossRatio: {},
  riskReward: {},
};

const formatCurrency = (value) => {
  const number = Number(value || 0);

  if (number === 0) return "$0";

  const sign = number > 0 ? "+" : "-";

  return `${sign}$${Math.abs(number).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const formatNumber = (value) => {
  return Number(value || 0).toLocaleString("en-US");
};

const formatPercent = (value) => {
  const number = Number(value || 0);

  if (number === 0) return "0%";

  const sign = number > 0 ? "+" : "";

  return `${sign}${number.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}%`;
};

const formatMinutes = (value) => {
  const totalMinutes = Number(value || 0);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (!hours) return `${minutes}m`;

  return `${hours}h ${minutes}m`;
};

const formatRatio = (value) => {
  const number = Number(value || 0);

  if (number === 0) return "0";

  return number.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const StatsSection = ({ section, __ }) => (
  <div className="trading-productivity-stats-card__section">
    <div className="trading-productivity-stats-card__section-title">
      {__(section.title)}
    </div>

    <div className="trading-productivity-stats-card__list">
      {section.rows.map((row, rowIndex) => (
        <div className="trading-productivity-stats-card__row" key={rowIndex}>
          <div className="trading-productivity-stats-card__label">
            {__(row.label)}
          </div>

          <div className="trading-productivity-stats-card__value">
            {row.value}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const TradingProductivityStatsCard = ({
  selectedFilter,
  stats,
  error,
  onFilterChange,
}) => {
  const { __ } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  const currentStats = stats || DEFAULT_STATS;

  const selectedOption =
    FILTER_OPTIONS.find((option) => option.value === selectedFilter) ||
    FILTER_OPTIONS[0];

  const handleSelect = (option) => {
    onFilterChange(option.value);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const centerStats = [
    {
      title: "trading_profile.pnl",
      rows: [
        {
          label: "trading_profile.total_pnl",
          value: formatCurrency(currentStats.pnl?.totalPnl),
        },
        {
          label: "trading_profile.total_profit",
          value: formatCurrency(currentStats.pnl?.totalProfit),
        },
        {
          label: "trading_profile.total_loss",
          value: formatCurrency(currentStats.pnl?.totalLoss),
        },
      ],
    },
    {
      title: "trading_profile.average_pnl",
      rows: [
        {
          label: "trading_profile.avg_pnl",
          value: formatCurrency(currentStats.averagePnl?.avgPnl),
        },
        {
          label: "trading_profile.avg_profit",
          value: formatCurrency(currentStats.averagePnl?.avgProfit),
        },
        {
          label: "trading_profile.avg_loss",
          value: formatCurrency(currentStats.averagePnl?.avgLoss),
        },
      ],
    },
    {
      title: "trading_profile.average_pnl_percent",
      rows: [
        {
          label: "trading_profile.avg_pnl_percent",
          value: formatPercent(currentStats.averagePnlPercent?.avgPnlPercent),
        },
        {
          label: "trading_profile.avg_pnl_percent_profit",
          value: formatPercent(
            currentStats.averagePnlPercent?.avgPnlPercentProfit,
          ),
        },
        {
          label: "trading_profile.avg_pnl_percent_loss",
          value: formatPercent(
            currentStats.averagePnlPercent?.avgPnlPercentLoss,
          ),
        },
      ],
    },
    {
      title: "trading_profile.holding_time",
      rows: [
        {
          label: "trading_profile.avg_holding_time",
          value: formatMinutes(currentStats.holdingTime?.avgHoldingTime),
        },
        {
          label: "trading_profile.avg_profitable_time",
          value: formatMinutes(currentStats.holdingTime?.avgProfitableTime),
        },
        {
          label: "trading_profile.avg_losing_time",
          value: formatMinutes(currentStats.holdingTime?.avgLosingTime),
        },
      ],
    },
  ];

  const bottomStats = [
    {
      title: "trading_profile.positions",
      rows: [
        {
          label: "trading_profile.closed_positions",
          value: formatNumber(currentStats.positions?.closedPositions),
        },
        {
          label: "trading_profile.profitable_positions",
          value: formatNumber(currentStats.positions?.profitablePositions),
        },
        {
          label: "trading_profile.losing_positions",
          value: formatNumber(currentStats.positions?.losingPositions),
        },
        {
          label: "trading_profile.breakeven_positions",
          value: formatNumber(currentStats.positions?.breakevenPositions),
        },
      ],
    },
    {
      title: "trading_profile.profit_loss_ratio",
      rows: [
        {
          label: "trading_profile.profit_ratio",
          value: formatRatio(currentStats.profitLossRatio?.profitRatio),
        },
        {
          label: "trading_profile.loss_ratio",
          value: formatRatio(currentStats.profitLossRatio?.lossRatio),
        },
        {
          label: "trading_profile.longest_profit_streak",
          value: formatNumber(
            currentStats.profitLossRatio?.longestProfitStreak,
          ),
        },
        {
          label: "trading_profile.longest_loss_streak",
          value: formatNumber(currentStats.profitLossRatio?.longestLossStreak),
        },
      ],
    },
    {
      title: "trading_profile.risk_reward",
      rows: [
        {
          label: "trading_profile.profit_factor",
          value: formatRatio(currentStats.riskReward?.profitFactor),
        },
        {
          label: "trading_profile.expected_value",
          value: formatCurrency(currentStats.riskReward?.expectedValue),
        },
        {
          label: "trading_profile.risk_reward_ratio",
          value: `${formatRatio(currentStats.riskReward?.riskRewardRatio)} : 1`,
        },
      ],
    },
  ];

  return (
    <div className="trading-productivity-stats-card">
      <div className="trading-productivity-stats-card__header">
        <h3 className="trading-productivity-stats-card__title">
          {__("trading_profile.productivity_statistics")}
        </h3>

        <div
          className="trading-productivity-stats-card__select-wrapper"
          ref={selectRef}
        >
          <div
            className="trading-productivity-stats-card__select"
            onClick={() => setIsOpen((prev) => !prev)}
          >
            <span className="trading-productivity-stats-card__select-value">
              {__(selectedOption.label)}
            </span>

            <span>
              {isOpen ? (
                <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M2.83348 9.9999L8.0008 6.03994L13.0215 10.3533"
                    stroke="white"
                    strokeWidth="1.5"
                  />
                </svg>
              ) : (
                <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M2.83348 6.00004L8.0008 9.96L13.0215 5.64668"
                    stroke="white"
                    strokeWidth="1.5"
                  />
                </svg>
              )}
            </span>
          </div>

          {isOpen && (
            <div className="trading-productivity-stats-card__dropdown">
              {FILTER_OPTIONS.map((option) => (
                <div
                  key={option.value}
                  className="trading-productivity-stats-card__option"
                  onClick={() => handleSelect(option)}
                >
                  <span>{__(option.label)}</span>

                  {selectedOption.value === option.value && (
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M13.3497 3.31274L6.32469 12.0752L3.42219 9.17774"
                        stroke="white"
                        strokeWidth="1.5"
                      />
                    </svg>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="trading-productivity-stats-card__error">
          Failed to load statistics
        </div>
      )}

      {!error && (
        <div className="trading-productivity-stats-card__body">
          <div className="trading-productivity-stats-card__body-content">
            <div className="trading-productivity-stats-card__center">
              {centerStats.map((section, index) => (
                <StatsSection key={index} section={section} __={__} />
              ))}
            </div>

            <div className="trading-productivity-stats-card__bottom">
              {bottomStats.map((section, index) => (
                <StatsSection key={index} section={section} __={__} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingProductivityStatsCard;